import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import Alert from './Alert';

const EditTextForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [textoId, setTextoId] = useState('');
  const [conteudo, setConteudo] = useState('');
  const [variaveis, setVariaveis] = useState([]);
  const [preview, setPreview] = useState('');
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    const fetchText = async () => {
      try {
        const response = await api.get(`/textos`);
        const text = response.data.find(t => t.id === parseInt(id));
        if (text) {
          setTextoId(text.texto_id);
          setConteudo(text.conteudo);
          setVariaveis(text.variaveis);
        }
      } catch (error) {
        console.error('Erro ao buscar texto:', error);
        setAlert({ message: 'Erro ao carregar o texto. Tente novamente.', type: 'error' });
      }
    };

    fetchText();
  }, [id]);

  useEffect(() => {
    updatePreview();
  }, [conteudo, variaveis]);

  const handleConteudoChange = (e) => {
    const newConteudo = e.target.value;
    setConteudo(newConteudo);
    
    const extractedVariaveis = newConteudo.match(/@(\w+)/g) || [];
    setVariaveis(extractedVariaveis.map(v => v.slice(1)));
  };

  const updatePreview = () => {
    let updatedPreview = conteudo;
    variaveis.forEach((variavel) => {
      const regex = new RegExp(`@${variavel}`, 'gi');
      updatedPreview = updatedPreview.replace(regex, `<span class="bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-300 px-1 rounded">${variavel}</span>`);
    });
    setPreview(updatedPreview);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/texto/${id}`, {
        texto_id: textoId,
        conteudo: conteudo,
        variaveis: variaveis
      });
      setAlert({ message: 'Texto atualizado com sucesso!', type: 'success' });
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      console.error('Erro ao atualizar texto:', error);
      setAlert({ message: 'Erro ao atualizar texto. Tente novamente.', type: 'error' });
    }
  };

  return (
    <div className="w-full">
      {alert && <Alert message={alert.message} type={alert.type} />}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Coluna de edição */}
          <div className="space-y-6 bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
            <div>
              <label htmlFor="texto-id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                ID do Texto
              </label>
              <input 
                type="text" 
                id="texto-id"
                value={textoId} 
                onChange={(e) => setTextoId(e.target.value)} 
                required 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 ease-in-out dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="conteudo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Conteúdo
              </label>
              <textarea 
                id="conteudo"
                value={conteudo} 
                onChange={handleConteudoChange} 
                required 
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 ease-in-out dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            <button 
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 ease-in-out"
            >
              Atualizar
            </button>
          </div>
          
          {/* Coluna de preview e variáveis */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Variáveis detectadas:</h3>
              {variaveis.length > 0 ? (
                <div className="flex flex-wrap gap-2 mb-4">
                  {variaveis.map((variavel, index) => (
                    <span key={index} className="inline-block bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-300 rounded-full px-3 py-1 text-sm font-medium">
                      @{variavel}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-sm">Nenhuma variável detectada. Use @VARIAVEL no texto para criar variáveis.</p>
              )}
            </div>
            
            <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Preview:</h3>
              <div 
                dangerouslySetInnerHTML={{ __html: preview }} 
                className="p-4 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-900 shadow-sm min-h-[200px] whitespace-pre-wrap dark:text-gray-300"
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditTextForm;